/**
 * Google Maps JavaScript API auth-failure diagnostics.
 *
 * When the Maps JS SDK fails to authorize (bad key, referrer not in the
 * allowlist, billing not enabled, API not enabled, etc.) it renders the
 * map in development mode with a translucent gray overlay reading
 * "This page can't load Google Maps correctly. Do you own this website?"
 * The actual error code is logged to the console as one of:
 *   - InvalidKeyMapError
 *   - RefererNotAllowedMapError
 *   - BillingNotEnabledMapError
 *   - ApiNotActivatedMapError
 *   - ExpiredKeyMapError
 * …but the message Google prints is terse and easy to miss in a noisy
 * console. The SDK also exposes a documented global hook,
 * `window.gm_authFailure`, that fires when auth fails — we use it to
 * print a single, prominent diagnostic that names the current origin
 * and points at the docs that describe the GCP-side fix.
 *
 * Idempotent — safe to call from any module-level entry point. Only the
 * first call wins; subsequent calls are no-ops so a hot-reloaded module
 * doesn't stomp the original handler.
 *
 * Reference: https://developers.google.com/maps/documentation/javascript/events#auth-errors
 */
declare global {
  interface Window {
    gm_authFailure?: () => void;
  }
}

let installed = false;

export function installGoogleMapsAuthFailureHandler(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.gm_authFailure = () => {
    const origin = window.location.origin;
    // eslint-disable-next-line no-console
    console.error(
      [
        '[Google Maps] Authentication failed — the Maps JavaScript API rejected the request.',
        `  Current origin: ${origin}`,
        '  Likely causes (in order of frequency):',
        `    1. HTTP-referrer restriction on the API key does not include "${origin}/*".`,
        '       Fix: GCP Console → APIs & Services → Credentials → edit the key →',
        '       Application restrictions → HTTP referrers → add the origin above.',
        '    2. Billing is not enabled on the GCP project tied to this key.',
        '    3. Maps JavaScript API is not enabled on this key (API restrictions).',
        '    4. Key is expired, deleted, or unrecognized.',
        '  See docs/google_maps_keys.md for the full setup + lockdown checklist.',
      ].join('\n'),
    );
  };
}
